import {HttpAxiosRequest} from 'src/http/httpAxiosBase'
import {
    CuentaContraparte,
    OperacionContraparte,
    schemaCuentasContraparte,
    schemaOperacionContraparte
} from '../types/types'
import {BaseResponse} from 'src/types'
import {
    OperacionContraparteCuentaDetalleUpdate,
    OperacionContraparteCuentaInsert,
    OperacionContraparteCuentaUpdate,
    OperacionContraparteInsert,
    OperacionContraparteLimitesAprobadosPais,
    OperacionContraparteUpdate,
    schemaListaOperacionContraparteLimitesAprobadosPais,
    schemaOperacionContraparteCuentaDetalleUpdate,
    schemaOperacionContraparteCuentaInsert,
    schemaOperacionContraparteCuentaUpdate,
    schemaOperacionContraparteInsert,
    schemaOperacionContraparteUpdate
} from '../types/dto'
import {HttpOperacionInforme} from "../../../api/httpOperacionInforme";
import {SistemaTipo} from "../../../../../types/enum/enum";
import {FormFiltros} from "../../../../limites/FiltrosBusqueda";

export const getEndpoint = (url = '') => `operaciones/contrapartes${url}`

export const HttpContraparte = {
    get: (idOperacion: number): Promise<OperacionContraparte> => {
        return HttpAxiosRequest.get(getEndpoint(`/${idOperacion}`))
            .then(response => {
                const parsed = schemaOperacionContraparte.safeParse(response)

                if (!parsed.success) {
                    console.error('Error de validación:', parsed.error.issues)

                    return Promise.reject(new Error('Error de validación.'))
                }

                return parsed.data
            })
            .catch(error => {
                console.error('Error de red:', error)

                return Promise.reject('Error de red.')
            })
    },

    put: (idOperacion: number, operacion: OperacionContraparteUpdate): Promise<BaseResponse> => {
        const parsed = schemaOperacionContraparteUpdate.safeParse(operacion)

        if (!parsed.success) {
            console.error('Error de validación:', parsed.error.issues)

            return Promise.reject(new Error('Error de validación.'))
        }

        return HttpAxiosRequest.put(getEndpoint(`/${idOperacion}`), parsed.data) // TODO: parsed.data, no operacion
            .then(response => {
                return Promise.resolve(response)
            })
            .catch(error => {
                console.error('Error de red:', error)

                return Promise.reject('Error de red.')
            })
    },

    post: (operacion: OperacionContraparteInsert): Promise<BaseResponse> => {
        const parsed = schemaOperacionContraparteInsert.safeParse(operacion)

        if (!parsed.success) {
            console.error('Error de validación:', parsed.error.issues)

            return Promise.reject(new Error('Error de validación.'))
        }

        return HttpAxiosRequest.post(getEndpoint(), parsed.data)
            .then(response => {
                return Promise.resolve(response)
            })
            .catch(error => {
                console.error('Error de red:', error)

                return Promise.reject('Error de red.')
            })
    },

    getCuentas: (idOperacion: number): Promise<CuentaContraparte[]> => {
        return HttpAxiosRequest.get(getEndpoint(`/${idOperacion}/cuentas`))
            .then(response => {
                const parsed = schemaCuentasContraparte.safeParse(response)

                if (!parsed.success) {
                    console.error('Error de validación:', parsed.error.issues)

                    return Promise.reject(new Error('Error de validación.'))
                }

                return parsed.data
            })
            .catch(error => {
                console.error('Error de red:', error)

                return Promise.reject('Error de red.')
            })
    },

    postCuenta: (idOperacion: number, cuenta: OperacionContraparteCuentaInsert): Promise<BaseResponse> => {
        const parsed = schemaOperacionContraparteCuentaInsert.safeParse(cuenta)

        if (!parsed.success) {
            console.error('Error de validación:', parsed.error.issues)

            return Promise.reject(new Error('Error de validación.'))
        }

        return HttpAxiosRequest.post(getEndpoint(`/${idOperacion}/cuentas`), parsed.data)
            .then(response => {
                return Promise.resolve(response)
            })
            .catch(error => {
                console.error('Error de red:', error)

                return Promise.reject('Error de red.')
            })
    },

    putCuenta: (
        idOperacion: number,
        idCuenta: number,
        detalle: OperacionContraparteCuentaUpdate
    ): Promise<BaseResponse> => {
        const parsed = schemaOperacionContraparteCuentaUpdate.safeParse(detalle)

        if (!parsed.success) {
            console.error('Error de validación:', parsed.error.issues)

            return Promise.reject(new Error('Error de validación.'))
        }

        return HttpAxiosRequest.put(getEndpoint(`/${idOperacion}/cuentas/${idCuenta}`), parsed.data)
            .then(response => {
                return Promise.resolve(response)
            })
            .catch(error => {
                console.error('Error de red:', error)

                return Promise.reject('Error de red.')
            })
    },

    deleteCuenta: (idOperacion: number, idCuenta: number): Promise<BaseResponse> => {
        return HttpAxiosRequest.delete(getEndpoint(`/${idOperacion}/cuentas/${idCuenta}`))
            .then(response => {
                return Promise.resolve(response)
            })
            .catch(error => {
                console.error('Error de red:', error)

                return Promise.reject('Error de red.')
            })
    },

    // Atención acá! Le venía llamando "detalle" a las cuentas, pero a partir de acá lo que antes
    // se llamaba "detalle" ahora es "cuenta". O sea, en la siguiente función, "idCuenta" es lo
    // que antes era "idDetalle", e "idDetalle" es el id de cada detalle DENTRO de una cuenta.
    // TODO: arreglar esta nomenclatura, claro.

    putCuentaDetalle: (
        idOperacion: number,
        idCuenta: number,
        idDetalle: number,
        detalle: OperacionContraparteCuentaDetalleUpdate
    ): Promise<BaseResponse> => {
        const parsed = schemaOperacionContraparteCuentaDetalleUpdate.safeParse(detalle)

        if (!parsed.success) {
            console.error('Error de validación:', parsed.error.issues)

            return Promise.reject(new Error('Error de validación.'))
        }

        return HttpAxiosRequest.put(
            getEndpoint(`/${idOperacion}/cuentas/${idCuenta}/detalles/${idDetalle}`),
            parsed.data
        )
            .then(res => Promise.resolve(res))
            .catch(err => Promise.reject(err))
    },

    descargarInforme: (idOperacion: number): Promise<any> =>
        HttpOperacionInforme.descargarInforme(idOperacion, SistemaTipo.Contrapartes),

    getLimites: (filtros: FormFiltros): Promise<OperacionContraparteLimitesAprobadosPais[]> => {
        return HttpAxiosRequest.getWithQueryParamsSerializer(getEndpoint(`/limites`), {...filtros})
            .then(response => {
                const parsed = schemaListaOperacionContraparteLimitesAprobadosPais.safeParse(response)

                if (!parsed.success) {
                    console.error('Error de validación:', parsed.error.issues)

                    return Promise.reject(new Error('Error de validación.'))
                }

                return parsed.data
            })
            .catch(error => {
                console.error('Error de red:', error)

                return Promise.reject('Error de red.')
            })
    },

} as const
